import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslationCategory } from '../services/ga-state.service';
import { ILanguage } from '../../../type_definitions/datatypes';
import { DEFAULT_LANGUAGE, Lang, LanguageDataService } from '../language/language-data.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import { ITranslate, TranslateDataService } from '../translation/translate-data.service';
import { FormattingService } from '../services/formatting.service';

import { TranslateService } from '@ngx-translate/core';
import * as _ from 'underscore';
import { EditableTextService, IEditableTextOption } from './editable-text.service';

interface IEditableTextModel {
  project?:string;
  category:TranslationCategory;
  language?:ILanguage;
  key?:string;
  text?:string;
  engText?:string;
  comments?:string;
}

export interface IEditableTextDialogBindings {
  key:string;                   // the key of the field (without screen or option id)
  initValue?:string;            // the initial value of the field
  project?:string;              // project name from Depronr (eg. 'GA2')
  category:TranslationCategory;// field category (eg. 'ga', 'bpprofiles', 'bpwizard', ..)
  languageId?:string;           // when languageId is undefined, take the language of the UI
  screen?:string;               // field screen (eg. 'app.userprofile', 'bpprofiles.bank', 'bpwizard.company_margin', ..); it's not needed when the full key is already constructed (eg. report 123)
  option?:IEditableTextOption;  // not needed when the full key is already constructed (eg. report 123)
  noDelete?:boolean;             // show/hide 'Delete' button, default shown
  onCancel:EventEmitter<void>;
  onSaved:EventEmitter<ITranslate>;
  onDeleted:EventEmitter<ITranslate>;
}

const GA2_PROJECT:string = 'GA2';

@Component({
  selector: 'editable-text-dialog',
  template: `
    <div class="message-dialog">
      <div class="modal-header">
        <h4 class="modal-title">{{ option?.name || 'Edit translation' }}</h4>
      </div>
      <div class="modal-body">
        <h3 *ngIf="loading">
          <activity-indicator useDefaultText="true"></activity-indicator>
        </h3>
        <formly *ngIf="!loading" [model]="model" [fields]="fields" [form]="form"></formly>
        <!-- sh command section -->
        <div *ngIf="languageId === '${Lang.EN}' && !loading">
          <span class="toggle-command">
            <a href="javascript:void(0)" (click)="toggleShowCommand()">π</a>
          </span>
          <div *ngIf="showCommand">
            <h3>Command
              <copy-to-clipboard element=".command-section" [statusMessage]="true"></copy-to-clipboard>
            </h3>
            <div class="code-section command-section" contenteditable="true">./manage-translations.sh -t translate-custom -j GA2 -c {{ model.category }} -k {{ model.key }} -i '{{ model.text }}'</div>
          </div>
        </div>
      </div>
      <div class="modal-footer footer-margin">
        <button class="btn btn-danger me-auto" [hidden]="noDelete" (click)="deleteTranslation()" [busyButton]="busy.delete" [disabled]="loading || busy.save"><i class="fa fa-trash-o"></i> <span class="ms-1" translate>GA.BUTTONS.DELETE</span></button>
        <button class="btn btn-success" (click)="save()" [busyButton]="busy.save" [disabled]="loading || busy.delete" translate>GA.BUTTONS.SAVE</button>
        <button (click)="onCancel.emit()" class="btn" translate>GA.BUTTONS.CANCEL</button>
      </div>
    </div>
  `
})
export class EditableTextDialogComponent implements OnInit, IEditableTextDialogBindings {
  @Input() public key:string;
  @Input() public initValue:string;
  @Input() public project:string;
  @Input() public category:TranslationCategory;
  @Input() public languageId:string;
  @Input() public screen:string;
  @Input() public option:IEditableTextOption;
  @Input() public noDelete:boolean;
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onSaved:EventEmitter<ITranslate> = new EventEmitter<ITranslate>();
  @Output() public onDeleted:EventEmitter<ITranslate> = new EventEmitter<ITranslate>();

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IEditableTextModel;
  public fields:IExtraFieldConfigurationObject[];

  private textField:IExtraFieldConfigurationObject;
  public busy:{ [ key:string ]:boolean } = {};
  public loading:boolean;
  public showCommand:boolean;
  private translationFound:boolean = false;

  constructor(
    private languageDataService:LanguageDataService,
    private translateDataService:TranslateDataService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translateService:TranslateService,
    private editableTextService:EditableTextService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.model = {
      project: this.project || GA2_PROJECT,
      category: this.category,
      key: this.editableTextService.getFullKey(this.key, this.screen, this.option?.id)
    };

    this.fields = this.createFields();

    this.loading = true;
    this.getTranslationLanguage()
      .then((language:ILanguage) => {
        this.model.language = language;
        this.textField.templateOptions.label = `${this.model.language.name} text`;
        return this.translateDataService.getTranslation(this.model.project, this.model.category, language.isoCode, this.model.key);
      })
      .then((translation:ITranslate) => {
        this.model = _.extend(this.model, translation);
        if (translation.text) {
          this.translationFound = true;
        }
      })
      .finally(() => {
        this.loading = false;

        // pre-filling with the initial value
        if (!this.translationFound) {
          this.model.text = this.initValue;
        }
      });
  }

  public save():void {
    if (this.form.invalid) {
      return;
    }

    this.busy.save = true;
    const translationData:ITranslate = {
      project: this.model.project,
      category: this.model.category,
      text: this.model.text,
      languageId: this.model.language.isoCode,
      key: this.model.key,
      comments: this.model.comments
    };

    const saveTranslationPromise:Promise<ITranslate> = this.translationFound? this.translateDataService.updateTranslation(translationData) : this.translateDataService.createTranslation(translationData);
    saveTranslationPromise.then((savedTranslation:ITranslate) => {
      this.onSaved.emit(savedTranslation);
    })
      .finally(() => {
        this.busy.save = false;
      });
  }

  public deleteTranslation():void {
    this.busy.delete = true;
    this.translateDataService.deleteTranslation(this.model.project, this.model.category, this.model.language.isoCode, this.model.key)
      .then(() => {
        // convert IEditableTextModel to ITranslate to be the same as onSaved()
        const data:ITranslate = <ITranslate>Object.assign({}, this.model);
        data.languageId = (<IEditableTextModel>data).language.isoCode;
        delete ((<IEditableTextModel>data).language);

        this.onDeleted.emit(data);
      })
      .finally(() => {
        this.busy.delete = false;
      });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-md-2';
    const valueClass:string = 'col-md-10';

    return [
      this.formlyService.aValue('project', 'Project')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .build(),

      this.formlyService.aValue('category', 'Category')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .build(),

      this.formlyService.aValue('key', 'Key')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withClipboard()
        .withoutEditable()
        .build(),

      this.formlyService.aValue('language', 'Language')
        .withFormat(this.formattingService.formatLanguage)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .build(),

      this.formlyService.aTextarea('engText', 'English text')
        .isDisabled()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .withHideExpression(() => {
          return !this.model.language || this.model.language.isoCode === DEFAULT_LANGUAGE;
        })
        .build(),

      this.textField = this.formlyService.aTextarea('text')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .build(),

      this.formlyService.aTextarea('comments', 'Comments')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutEditable()
        .build()
    ];
  }

  private getTranslationLanguage():Promise<ILanguage> {
    return this.languageDataService.getLanguages()
      .then((languages:ILanguage[]) => {
        const language:string = this.languageId || this.translateService.getDefaultLang();
        return _.findWhere(languages, { isoCode: language });
      });
  }

  public toggleShowCommand():void {
    this.showCommand = !this.showCommand;
  }
}